import {
  BulkCashEntryTransaction,
  BulkDirectTransferTransaction,
  BulkFXForwardTransaction,
  BulkFXSpotTransaction,
  BulkLoadTransaction,
  BulkTradeEntryTransaction,
  BulkTransaction,
  BulkWireTransferTransaction,
  getTransactionCategoryFromTypeKey,
  toISOFormatFromAnyValidDateString,
  TranCategory,
} from '@helios/create-transactions-modal'
import { BulkClearwaterTransaction } from '../models/clearwater-transaction.model'

/**
 * Call relevant wrapper to convert transaction cw to dialog transaction for edit based on given category
 */
export function mapBulkClearwaterTransactionsToBulkTransactions(
  cwTransactions: BulkClearwaterTransaction[],
): BulkTransaction[] | undefined {
  const tranCategory = getTransactionCategoryFromTypeKey(cwTransactions[0].type)
  switch (tranCategory) {
    case TranCategory.CASH_ENTRY:
      return cwTransactions.map(cwTransaction => {
        return mapBulkClearwaterTransactionToBulkCashEntryTransaction(
          cwTransaction,
        )
      })
    case TranCategory.TRADE:
      return cwTransactions.map(cwTransaction => {
        return mapBulkClearwaterTransactionToBulkTradeTransaction(cwTransaction)
      })
    case TranCategory.CURRENCY_FORWARD:
      return cwTransactions.map(cwTransaction => {
        return mapBulkClearwaterTransactionToBulkFxForwardTransaction(
          cwTransaction,
        )
      })
    case TranCategory.FX_TRADE:
      return cwTransactions.map(cwTransaction => {
        return mapBulkClearwaterTransactionToBulkFxSpotTransaction(
          cwTransaction,
        )
      })
    case TranCategory.DIRECT_TRANSFER:
      return cwTransactions.map(cwTransaction => {
        return mapBulkClearwaterTransactionToBulkWireTransaction(cwTransaction)
      })

    case TranCategory.TRANSFER: {
      if (
        cwTransactions[0].destAccountId &&
        cwTransactions[0].destAccountId != -1
      ) {
        return cwTransactions.map(cwTransaction => {
          return mapBulkClearwaterTransactionToBulkDirectTransfer(cwTransaction)
        })
      } else {
        return cwTransactions.map(cwTransaction => {
          return mapBulkClearwaterTransactionToBulkTransfer(cwTransaction)
        })
      }
    }
  }
  return undefined
}
/**
 * convert trade cwTransaction into trade bulk dialog edit transaction
 */
export function mapBulkClearwaterTransactionToBulkTradeTransaction(
  cwTransaction: BulkClearwaterTransaction,
): BulkTradeEntryTransaction {
  return {
    postDate: toISOFormatFromAnyValidDateString(cwTransaction.postDate ?? ''),
    amount: cwTransaction.amount,
    cusip: cwTransaction.secIdentifier,
    commission: cwTransaction.commission ?? 0.0,
    originationFee: cwTransaction.originationFee,
    prepaidInterest: cwTransaction.prepaidInterest,
    interestDue: cwTransaction.interestDue,
    yield: cwTransaction.yield ?? 0.0,
    counterPartyId: cwTransaction.counterPartyId ?? 13,
    price: cwTransaction.price,
    units: cwTransaction.units,
    accrued: cwTransaction.accrued,
    factor: cwTransaction.factor,
    clearedDate: cwTransaction.clearedDate,
    inventoryType: cwTransaction.inventoryType,
    securityId: cwTransaction.securityId,
    transactionId: cwTransaction.transactionId,
    legacyOrigTranId: cwTransaction.originalId,
    legacyTranId: cwTransaction.id,
    amortOverrideType: cwTransaction.amortOverrideType,
    tranType: cwTransaction.type,
    createdDate: cwTransaction.createdDate.toString(),
    userId: cwTransaction.userId,
    accountId: cwTransaction.accountId,
    entryDate: toISOFormatFromAnyValidDateString(cwTransaction.entryDate ?? ''),
    settleDate: toISOFormatFromAnyValidDateString(
      cwTransaction.settleDate ?? '',
    ),
    counterParty: cwTransaction.counterParty,
    traderId: cwTransaction.traderId,
    revisionId: cwTransaction.revisionId,
    typeLabelId: Number(cwTransaction.typeLabelId),
    dataMap: cwTransaction.dataMap,
    isCapitalize: cwTransaction.isCapitalize,
    orderId: cwTransaction.searchByIdIndex?.toString(),
  }
}
/**
 * convert trade cwTransaction into FxForward bulk dialog edit transaction
 */
export function mapBulkClearwaterTransactionToBulkFxForwardTransaction(
  cwTransaction: BulkClearwaterTransaction,
): BulkFXForwardTransaction {
  return {
    postDate: toISOFormatFromAnyValidDateString(cwTransaction.postDate ?? ''),
    fromSecurityId: Number(cwTransaction.fromSecurityId),
    toSecurityId: Number(cwTransaction.toSecurityId),
    fromUnits: -1 * Math.abs(Number(cwTransaction.fromUnits)),
    toUnits: Math.abs(Number(cwTransaction.toUnits)),
    counterPartyId: cwTransaction.counterPartyId ?? 13,
    sellToBuyRate: Number(cwTransaction.sellToBuyRate),
    transactionId: cwTransaction.transactionId,
    legacyOrigTranId: cwTransaction.originalId,
    legacyTranId: cwTransaction.id,
    amortOverrideType: cwTransaction.amortOverrideType,
    tranType: cwTransaction.type,
    createdDate: cwTransaction.createdDate.toString(),
    userId: cwTransaction.userId,
    accountId: cwTransaction.accountId,
    entryDate: toISOFormatFromAnyValidDateString(cwTransaction.entryDate ?? ''),
    settleDate: toISOFormatFromAnyValidDateString(
      cwTransaction.settleDate ?? '',
    ),
    traderId: cwTransaction.traderId,
    revisionId: cwTransaction.revisionId,
    toSecurityCusip: cwTransaction.secIdentifier,
    fromSecurityCusip: cwTransaction.fromSecIdentifier,
    typeLabelId: Number(cwTransaction.typeLabelId),
    counterParty: cwTransaction.counterParty,
    dataMap: cwTransaction.dataMap,
    orderId: cwTransaction.searchByIdIndex?.toString(),
    clearedDate: cwTransaction.clearedDate,
  }
}
/**
 * convert trade cwTransaction into FxSpot bulk dialog edit transaction
 */
export function mapBulkClearwaterTransactionToBulkFxSpotTransaction(
  cwTransaction: BulkClearwaterTransaction,
): BulkFXSpotTransaction {
  return {
    postDate: toISOFormatFromAnyValidDateString(cwTransaction.postDate ?? ''),
    fromSecurityId: Number(cwTransaction.fromSecurityId),
    toSecurityId: Number(cwTransaction.toSecurityId),
    fromUnits: Math.abs(Number(cwTransaction.fromUnits)),
    toUnits: Math.abs(Number(cwTransaction.toUnits)),
    counterPartyId: cwTransaction.counterPartyId ?? 13,
    sellToBuyRate: Number(cwTransaction.sellToBuyRate),
    securityId: cwTransaction.securityId,
    transactionId: cwTransaction.transactionId,
    legacyOrigTranId: cwTransaction.originalId,
    legacyTranId: cwTransaction.id,
    amortOverrideType: cwTransaction.amortOverrideType,
    tranType: cwTransaction.type,
    createdDate: cwTransaction.createdDate.toString(),
    userId: cwTransaction.userId,
    accountId: cwTransaction.accountId,
    entryDate: toISOFormatFromAnyValidDateString(cwTransaction.entryDate ?? ''),
    settleDate: toISOFormatFromAnyValidDateString(
      cwTransaction.settleDate ?? '',
    ),
    traderId: cwTransaction.traderId,
    revisionId: cwTransaction.revisionId,
    typeLabelId: Number(cwTransaction.typeLabelId),
    counterParty: cwTransaction.counterParty,
    dataMap: cwTransaction.dataMap,
    orderId: cwTransaction.searchByIdIndex?.toString(),
  }
}

/**
 * convert cash entry cwTransaction into cash entry bulk dialog edit transaction
 */
export function mapBulkClearwaterTransactionToBulkCashEntryTransaction(
  cwTransaction: BulkClearwaterTransaction,
): BulkCashEntryTransaction {
  return {
    postDate: toISOFormatFromAnyValidDateString(cwTransaction.postDate ?? ''),
    amount: cwTransaction.amount,
    active: cwTransaction.active,
    tranType: cwTransaction.type,
    clearedDate: cwTransaction.clearedDate,
    createdDate: cwTransaction.createdDate.toString(),
    userId: cwTransaction.userId,
    accountId: cwTransaction.accountId,
    entryDate: toISOFormatFromAnyValidDateString(cwTransaction.entryDate ?? ''),
    settleDate: toISOFormatFromAnyValidDateString(
      cwTransaction.settleDate ?? '',
    ),
    revisionId: cwTransaction.revisionId,
    securityId: cwTransaction.securityId,
    cusip: cwTransaction.secIdentifier,
    currencyCode: cwTransaction.currency,
    transactionId: cwTransaction.transactionId,
    legacyOrigTranId: cwTransaction.originalId,
    legacyTranId: cwTransaction.id,
    typeLabelId: Number(cwTransaction.typeLabelId),
    dataMap: cwTransaction.dataMap,
    orderId: cwTransaction.searchByIdIndex?.toString(),
  }
}

/**
 * convert cash entry cwTransaction into cash entry bulk dialog edit transaction
 */
export function mapBulkClearwaterTransactionToBulkWireTransaction(
  cwTransaction: BulkClearwaterTransaction,
): BulkWireTransferTransaction {
  return {
    units: cwTransaction.units,
    active: cwTransaction.active,
    tranType: cwTransaction.type,
    createdDate: cwTransaction.createdDate.toString(),
    userId: cwTransaction.userId,
    accountId: cwTransaction.accountId,
    sourceAccountId: cwTransaction.sourceAccountId ?? cwTransaction.accountId,
    destAccountId: cwTransaction.destAccountId,
    entryDate: toISOFormatFromAnyValidDateString(cwTransaction.entryDate ?? ''),
    settleDate: toISOFormatFromAnyValidDateString(
      cwTransaction.settleDate ?? '',
    ),
    sourcePostDate: toISOFormatFromAnyValidDateString(
      cwTransaction.settleDate ?? '',
    ),
    destPostDate: toISOFormatFromAnyValidDateString(
      cwTransaction.settleDate ?? '',
    ),
    sourceClearedDate: cwTransaction.sourceClearedDate ?? '',
    destClearedDate: cwTransaction.destClearedDate ?? '',
    revisionId: cwTransaction.revisionId,
    securityId: cwTransaction.securityId,
    transactionId: cwTransaction.transactionId,
    legacyOrigTranId: cwTransaction.originalId,
    legacyTranId: cwTransaction.id,
    typeLabelId: Number(cwTransaction.typeLabelId),
    dataMap: cwTransaction.dataMap,
    orderId: cwTransaction.searchByIdIndex?.toString(),
    transactionBasis: cwTransaction.basis,
  }
}
/**
 * convert cash entry cwTransaction into cash entry bulk dialog edit transaction
 */
export function mapBulkClearwaterTransactionToBulkTransfer(
  cwTransaction: BulkClearwaterTransaction,
): BulkLoadTransaction {
  return {
    units: cwTransaction.units,
    active: true,
    tranType: cwTransaction.type,
    createdDate: cwTransaction.createdDate
      ? toISOFormatFromAnyValidDateString(cwTransaction.createdDate)
      : '',
    userId: cwTransaction.userId ?? 0,
    accountId: cwTransaction.accountId,
    entryDate: toISOFormatFromAnyValidDateString(cwTransaction.entryDate ?? ''),
    settleDate: toISOFormatFromAnyValidDateString(
      cwTransaction.settleDate ?? '',
    ),
    clearedDate: cwTransaction.clearedDate
      ? toISOFormatFromAnyValidDateString(cwTransaction.clearedDate)
      : undefined,
    revisionId: cwTransaction.revisionId,
    securityId: cwTransaction.securityId,
    transactionId: cwTransaction.transactionId,
    legacyOrigTranId: cwTransaction.originalId,
    legacyTranId: cwTransaction.id,
    // PVAL-26104: carry the amount the transaction already has, exactly as the
    // cash-entry and trade mappers above already do. The Bulk Load dialog reads
    // this off `originalTransactions` and falls back to it when a derive cycle
    // produces no amount; without it that fallback silently no-ops on the Bulk
    // Transaction Panel path and an omitted-derive save still zeroes the cash
    // leg (the Clearwater Transaction Panel path goes through
    // mapLoadToBulkLoadTransaction, which now copies it too).
    amount: cwTransaction.amount,
    transferPrice: cwTransaction.transferPrice ?? 0,
    clientLotId: cwTransaction.clientLotId,
    dataMap: cwTransaction.dataMap,
    inventoryType: cwTransaction.inventoryType,
    factor: cwTransaction.factor,
    postDate: toISOFormatFromAnyValidDateString(cwTransaction.postDate ?? ''),
    counterPartyId: cwTransaction.counterPartyId ?? 13,
    counterParty: cwTransaction.counterParty,
    traderId: cwTransaction.traderId,
    cusip: cwTransaction.secIdentifier,
    orderId: cwTransaction.searchByIdIndex?.toString(),
    transactionBasis: cwTransaction.basis,
  }
}

/**
 * convert direct transfer cwTransaction into direct transfer bulk dialog edit transaction
 */
export function mapBulkClearwaterTransactionToBulkDirectTransfer(
  cwTransaction: BulkClearwaterTransaction,
): BulkDirectTransferTransaction {
  return {
    units: cwTransaction.units ?? 0,
    active: true,
    tranType: cwTransaction.type,
    createdDate: cwTransaction.createdDate
      ? toISOFormatFromAnyValidDateString(cwTransaction.createdDate)
      : '',
    userId: cwTransaction.userId ?? 0,
    accountId: cwTransaction.accountId,
    entryDate: toISOFormatFromAnyValidDateString(cwTransaction.entryDate ?? ''),
    settleDate: toISOFormatFromAnyValidDateString(
      cwTransaction.settleDate ?? '',
    ),
    clearedDate: cwTransaction.clearedDate
      ? toISOFormatFromAnyValidDateString(cwTransaction.clearedDate)
      : undefined,
    revisionId: cwTransaction.revisionId,
    securityId: cwTransaction.securityId,
    transactionId: cwTransaction.transactionId,
    legacyOrigTranId: cwTransaction.originalId,
    legacyTranId: cwTransaction.id,
    transferPrice: cwTransaction.transferPrice ?? 0,
    dataMap: cwTransaction.dataMap,
    //inventoryType: cwTransaction.inventoryType,
    factor: cwTransaction.factor,
    postDate: toISOFormatFromAnyValidDateString(cwTransaction.postDate ?? ''),
    counterPartyId: cwTransaction.counterPartyId ?? 13,
    counterParty: cwTransaction.counterParty,
    traderId: cwTransaction.traderId,
    destAccountId: cwTransaction.destAccountId,
    cusip: cwTransaction.secIdentifier,
    orderId: cwTransaction.searchByIdIndex?.toString(),
    transactionBasis: cwTransaction.basis,
  }
}
